import { Box, Button, Checkbox, FormControl, FormLabel, HStack, Input, Radio, RadioGroup, Stack, Text, useToast, VStack } from "@chakra-ui/react";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { quit } from "../../api";
import LogInOnly from "../../components/Auth/LogInOnly";
import { colors } from "../../styles";
import { forceLogout, getApiErrorCode, getApiErrorMessage } from "../../utils";

const QUIT_REASON_OPTIONS = [
    { value: "약국이나 제약사 폐업", label: "폐업" },
    { value: "약국이나 제약사 퇴사", label: "양도" },
    { value: "서비스 불만족", label: "서비스 불만족" },
    { value: "직접 입력", label: "기타" },
] as const;

const CUSTOM_QUIT_VALUE = "직접 입력" as const;

export default function Quit() {

    /* state - errorStep */

    const [errorStep, setErrorStep] = useState(0);

    /* navigate */

    const navigate = useNavigate();

    /* toast */

    const toast = useToast();

    /* interface - IPasswordForm */

    interface IPasswordForm {
        password : string;
    }

    const {register, handleSubmit, formState: {errors}, watch} = useForm<IPasswordForm>();

    ////////////////////////////////////////////////////////////////////// 

    const isPasswordFilled = Boolean(watch("password"));

    ////////////////////////////////////////////////////////////////////// 

    const [selectedQuitReason, setSelectedQuitReason] = useState("");
    const [customQuitReason, setCustomQuitReason] = useState("");

    const quitReason =
        selectedQuitReason === CUSTOM_QUIT_VALUE
            ? customQuitReason.trim() || null
            : selectedQuitReason || null;

    const handleQuitReasonChange = (next: string) => {
        setSelectedQuitReason(next);
        setErrorStep((s) => (s === 2 ? 0 : s));
    };

    ////////////////////////////////////////////////////////////////////// 체크박스

    const [isFirstAgreeChecked, setIsFirstAgreeChecked] = useState(false);
    const [isSecondAgreeChecked, setIsSecondAgreeChecked] = useState(false);

    ////////////////////////////////////////////////////////////////////// 

    const onSubmit = async (data:IPasswordForm) => {

        ////////////////////////////////////////////////////////////////////// 

        const { password } = data;

        ////////////////////////////////////////////////////////////////////// 비밀번호 확인

        if(!isPasswordFilled) setErrorStep(1);
        if (!quitReason) setErrorStep(2);
        if(!isFirstAgreeChecked) setErrorStep(3);
        if(!isSecondAgreeChecked) setErrorStep(4);
        if(!isPasswordFilled || !quitReason || !isFirstAgreeChecked || !isSecondAgreeChecked) return;

        ////////////////////////////////////////////////////////////////////// 탈퇴 완료

        try {
            await quitMutation.mutateAsync({
                password,
                quitReason,
            });
        } catch {
            // mutation onError에서 서버 code/message를 사용자 문구로 매핑합니다.
        }

    }

    ////////////////////////////////////////////////////////////////////// 

    const logOut = async () => {
        forceLogout();
        navigate("/");
    }
    
    const quitMutation = useMutation({
        mutationFn: quit,
        onSuccess: () => {
            console.log('탈퇴 성공');
            toast({
                title: '탈퇴 완료',
                description: '탈퇴 완료',
                status: 'success',
                duration: 2000,
                isClosable: true,
            });
            logOut();
        },
        onError: (error) => {
            console.log('탈퇴 실패', error);
            toast({
                title: getQuitErrorMessage(error),
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }
    })

    const getQuitErrorMessage = (error: unknown) => {
        // 서버 code: INVALID_PASSWORD | QUIT_NOT_ALLOWED | USER_ALREADY_QUIT
        switch (getApiErrorCode(error)) {
            case "INVALID_PASSWORD":
                return "비밀번호가 일치하지 않습니다.";
            case "QUIT_NOT_ALLOWED":
                return "현재 상태에서는 탈퇴할 수 없습니다.";
            case "USER_ALREADY_QUIT":
                return "이미 탈퇴 처리된 계정입니다.";
            default:
                return getApiErrorMessage(error, "회원 탈퇴 중 문제가 발생했습니다.");
        }
    };

    ////////////////////////////////////////////////////////////////////// 

    return (
        <LogInOnly>
            <Stack justifyContent={"center"} alignItems={"center"} width={"100%"} paddingY={20}>
                <Stack 
                    width={{
                        base:"100%",
                        sm:"100%",
                        md:"60%",
                        lg:"40%",
                        xl:"40%"
                    }} 
                >
                    <VStack  
                        padding={1}
                        spacing={0}
                        alignItems={"flex-start"}
                        width={"100%"} 
                    >
                        <Text fontSize={28} fontWeight={700} color={colors.g1}>회원 탈퇴</Text>
                    </VStack>
                    <VStack 
                        marginTop={5}
                        padding={5}
                        alignItems="stretch"
                    >
                        <FormLabel fontSize="sm" color={colors.blue}>
                            비밀번호 입력
                        </FormLabel>
                        <FormControl marginTop={5}>
                            <Input 
                                {...register("password", {required:true})} 
                                type="password" 
                                isInvalid={Boolean(errors.password?.message)} 
                                placeholder={"비밀번호 입력"}
                                borderBottom={`1px solid ${colors.g4}`} 
                                borderTop="none" 
                                borderLeft="none" 
                                borderRight="none" 
                                borderRadius="0" 
                                _focus={{
                                    borderBottom: `1px solid ${colors.blue}`,
                                    boxShadow: "none",
                                    outline: "none"
                                }}
                            />
                        </FormControl>

                        <FormLabel fontSize="sm" color={colors.blue} marginTop={10}>
                            사유를 선택해주세요
                        </FormLabel>
                        <RadioGroup width="100%" value={selectedQuitReason} onChange={handleQuitReasonChange}>
                            <Stack spacing={2} width="100%" marginTop={5}>
                                {QUIT_REASON_OPTIONS.map(({ value, label }) => (
                                    <Box
                                        key={value}
                                        as="label"
                                        cursor="pointer"
                                        width="100%"
                                        borderRadius={10}
                                        px={5}
                                        py={4}
                                        bg={selectedQuitReason === value ? colors.light : colors.g5}
                                        transition="background 0.15s ease"
                                    >
                                        <HStack spacing={3} align="center">
                                            <Radio value={value} colorScheme="blue" />
                                            <Text fontSize={16} fontWeight={700} color={colors.g1}>
                                                {label}
                                            </Text>
                                        </HStack>
                                    </Box>
                                ))}
                            </Stack>
                        </RadioGroup>

                        {selectedQuitReason === CUSTOM_QUIT_VALUE && (
                            <FormControl marginTop={5}>
                                <Input 
                                    value={customQuitReason}
                                    onChange={(e) => {
                                        setCustomQuitReason(e.target.value);
                                        setErrorStep((s) => (s === 2 ? 0 : s));
                                    }}
                                    placeholder="탈퇴 사유를 직접 입력해주세요"
                                    borderBottom={`1px solid ${colors.g4}`} 
                                    borderTop="none" 
                                    borderLeft="none" 
                                    borderRight="none" 
                                    borderRadius="0" 
                                    _focus={{
                                        borderBottom: `1px solid ${colors.blue}`,
                                        boxShadow: "none",
                                        outline: "none"
                                    }}
                                />
                            </FormControl>
                        )}

                        <VStack width="100%" marginTop={10} spacing={4}>
                            <HStack width="100%" borderRadius={10} justifyContent={"flex-start"}>
                                <Checkbox
                                    isChecked={isFirstAgreeChecked}
                                    onChange={(e) => setIsFirstAgreeChecked(e.target.checked)}
                                    colorScheme="blue"
                                    size="lg"
                                >
                                    <Text fontSize={14} fontWeight={500} color={colors.g1}>
                                        회원 탈퇴 시 데이터를 복구할 수 없음을 인지했습니다.
                                    </Text>
                                </Checkbox>
                            </HStack>
                            <HStack width="100%" borderRadius={10} justifyContent={"flex-start"}>
                                <Checkbox
                                    isChecked={isSecondAgreeChecked}
                                    onChange={(e) => setIsSecondAgreeChecked(e.target.checked)}
                                    colorScheme="blue"
                                    size="lg"
                                >
                                    <Text fontSize={14} fontWeight={500} color={colors.g1}>
                                        회원 탈퇴 후, 재가입 이전에는 팜챗 서비스 이용이 불가함을 인지했습니다.
                                    </Text>
                                </Checkbox>
                            </HStack>
                        </VStack>
                        
                        {errorStep === 1 ? (<Text color={"red.500"} textAlign={"start"} fontSize={"sm"}> 비밀번호를 입력해주세요. </Text>) : null}
                        {errorStep === 2 ? (<Text color={"red.500"} textAlign={"start"} fontSize={"sm"}> 사유를 선택해주세요. </Text>) : null}
                        {errorStep === 3 ? (<Text color={"red.500"} textAlign={"start"} fontSize={"sm"}> 체크박스를 확인해주세요. </Text>) : null}
                        {errorStep === 4 ? (<Text color={"red.500"} textAlign={"start"} fontSize={"sm"}> 체크박스를 확인해주세요. </Text>) : null}

                        <Button 
                            width={"100%"} 
                            height={"52px"}
                            backgroundColor={colors.blue} 
                            color={"white"} 
                            borderRadius={10}
                            marginTop={10}
                            onClick={handleSubmit(onSubmit)}
                            disabled={!isPasswordFilled || !quitReason || !isFirstAgreeChecked || !isSecondAgreeChecked}
                            isLoading={quitMutation.isPending}
                        >
                            <Text fontSize={16} fontWeight={700}>탈퇴하기</Text>
                        </Button>
                    </VStack>
                </Stack>
            </Stack>
        </LogInOnly>
    )
}